<template>
  <div
    ref="rootRef"
    class="calculator-select"
    :class="{
      'calculator-select--open': isOpen,
      'calculator-select--disabled': disabled
    }"
  >
    <span :id="labelId" class="visually-hidden">{{ accessibleLabel }}</span>
    <button
      :id="triggerId"
      ref="triggerRef"
      type="button"
      class="calculator-select__trigger"
      :disabled="disabled"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-labelledby="`${labelId} ${valueId}`"
      :aria-describedby="ariaDescribedby"
      :data-testid="dataTestid"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span
        :id="valueId"
        class="calculator-select__value"
        :class="{ 'is-placeholder': !selectedOption }"
      >
        {{ selectedDisplayText }}
      </span>
      <FontAwesomeIcon
        class="chevron-icon"
        :icon="`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`"
      />
    </button>

    <div v-if="isOpen" class="calculator-select__popup">
      <div v-if="searchable" class="calculator-select__search">
        <svg
          class="calculator-select__search-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M6.875 12.25C8.06797 12.25 9.16875 11.8613 10.0617 11.2055L14.4281 15.5719C14.7406 15.8844 15.2469 15.8844 15.5562 15.5719C15.8656 15.2594 15.8688 14.7531 15.5562 14.4438L11.1898 10.0773C11.8457 9.18437 12.2344 8.08359 12.2344 6.89062C12.2344 3.9125 9.82187 1.5 6.84375 1.5C3.86562 1.5 1.45312 3.9125 1.45312 6.89062C1.45312 9.86875 3.86562 12.2812 6.84375 12.2812L6.875 12.25ZM6.875 3.1C8.96875 3.1 10.6656 4.79688 10.6656 6.89062C10.6656 8.98438 8.96875 10.6812 6.875 10.6812C4.78125 10.6812 3.08437 8.98438 3.08437 6.89062C3.08437 4.79688 4.78125 3.1 6.875 3.1Z"
            fill="currentColor"
          />
        </svg>
        <input
          :id="searchId"
          ref="searchRef"
          type="text"
          role="combobox"
          class="calculator-select__search-input"
          autocomplete="off"
          aria-autocomplete="list"
          :aria-controls="listboxId"
          :aria-expanded="isOpen"
          :aria-activedescendant="activeOptionId"
          :aria-label="accessibleLabel"
          :data-testid="dataTestid ? `${dataTestid}-search` : undefined"
          :value="query"
          @input="onSearchInput"
          @keydown="onSearchKeydown"
        />
      </div>

      <ul
        :id="listboxId"
        ref="listboxRef"
        class="calculator-select__listbox"
        role="listbox"
        :tabindex="searchable ? undefined : -1"
        :aria-label="accessibleLabel"
        :aria-activedescendant="searchable ? undefined : activeOptionId"
        @keydown="
          (event) => {
            if (!searchable) onListboxKeydown(event);
          }
        "
      >
        <li
          v-for="(option, index) in filteredOptions"
          :id="optionId(index)"
          :key="option.value"
          class="calculator-select__option"
          :class="{
            'is-active': index === activeIndex,
            'is-selected': option.value === modelValue
          }"
          role="option"
          :aria-selected="option.value === modelValue"
          @click="selectOption(option)"
          @mousemove="activeIndex = index"
        >
          <span class="calculator-select__option-text">{{ option.text }}</span>
          <span v-if="option.description" class="calculator-select__option-description">
            {{ option.description }}
          </span>
        </li>
      </ul>

      <p
        v-if="searchable && filteredOptions.length === 0"
        class="calculator-select__empty"
        :data-testid="dataTestid ? `${dataTestid}-empty` : undefined"
      >
        No matches found.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs } from "vue";

export interface CalculatorSelectOption {
  value: string;
  text: string;
  description?: string;
  selectedText?: string;
}

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    id: string;
    modelValue: string | null;
    options: CalculatorSelectOption[];
    placeholder: string;
    accessibleLabel: string;
    disabled?: boolean;
    searchable?: boolean;
    ariaDescribedby?: string;
  }>(),
  { disabled: false, searchable: false, ariaDescribedby: undefined }
);

const emit = defineEmits<{
  (event: "update:modelValue", value: string | null): void;
  (event: "open" | "close"): void;
}>();

const attrs = useAttrs();
const dataTestid = computed(() => attrs["data-testid"] as string | undefined);

const triggerId = computed(() => `${props.id}-trigger`);
const labelId = computed(() => `${props.id}-label`);
const valueId = computed(() => `${props.id}-value`);
const listboxId = computed(() => `${props.id}-listbox`);
const searchId = computed(() => `${props.id}-search`);
const optionId = (index: number): string => `${props.id}-option-${index}`;

const rootRef = ref<HTMLDivElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);
const listboxRef = ref<HTMLUListElement | null>(null);
const searchRef = ref<HTMLInputElement | null>(null);

const isOpen = ref(false);
const activeIndex = ref(-1);
const query = ref("");

const selectedIndex = computed(() =>
  props.options.findIndex((option) => option.value === props.modelValue)
);
const selectedOption = computed(() =>
  selectedIndex.value >= 0 ? props.options[selectedIndex.value] : null
);
const selectedDisplayText = computed(() =>
  selectedOption.value
    ? (selectedOption.value.selectedText ?? selectedOption.value.text)
    : props.placeholder
);
const filteredOptions = computed(() => {
  if (!props.searchable || query.value.trim() === "") return props.options;
  const needle = query.value.trim().toLowerCase();
  return props.options.filter((option) => option.text.toLowerCase().includes(needle));
});
const activeOptionId = computed(() =>
  isOpen.value && activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined
);

// Deliberate deviation from the brief's literal snippet: activeIndex stays -1
// after typing (even when there are matches) instead of presetting to 0. With
// query text that matches multiple options (e.g. "engineering" matching both
// "Aerospace Engineering" and "Human Systems Engineering"), presetting to 0
// makes a single ArrowDown land on the *second* match, not the first -- see
// task-2-report.md for the reasoning and the failing-test evidence.
const onSearchInput = (event: Event): void => {
  query.value = (event.target as HTMLInputElement).value;
  activeIndex.value = -1;
};

const open = (): void => {
  if (props.disabled || isOpen.value) return;
  query.value = "";
  isOpen.value = true;
  const initial = filteredOptions.value.findIndex((o) => o.value === props.modelValue);
  activeIndex.value = filteredOptions.value.length === 0 ? -1 : initial >= 0 ? initial : 0;
  emit("open");
  void nextTick(() => {
    (props.searchable ? searchRef.value : listboxRef.value)?.focus();
  });
};

const close = (returnFocus = false): void => {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit("close");
  if (returnFocus) void nextTick(() => triggerRef.value?.focus());
};

const toggle = (): void => {
  if (props.disabled) return;
  if (isOpen.value) close(true);
  else open();
};

const selectOption = (option: CalculatorSelectOption): void => {
  emit("update:modelValue", option.value);
  close(true);
};

const onTriggerKeydown = (event: KeyboardEvent): void => {
  if (["ArrowDown", "ArrowUp", "Enter", " ", "Spacebar"].includes(event.key)) {
    event.preventDefault();
    open();
  } else if (event.key === "Escape") {
    close();
  }
};

let typeBuffer = "";
let typeTimer: ReturnType<typeof setTimeout> | undefined;
const typeahead = (char: string): void => {
  typeBuffer += char.toLowerCase();
  if (typeTimer) clearTimeout(typeTimer);
  typeTimer = setTimeout(() => (typeBuffer = ""), 500);
  const match = filteredOptions.value.findIndex((option) =>
    option.text.toLowerCase().startsWith(typeBuffer)
  );
  if (match >= 0) activeIndex.value = match;
};

const onListboxKeydown = (event: KeyboardEvent): void => {
  const last = filteredOptions.value.length - 1;
  if (event.key === "ArrowDown") {
    event.preventDefault();
    activeIndex.value = Math.min(last, activeIndex.value + 1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    activeIndex.value = Math.max(0, activeIndex.value - 1);
  } else if (event.key === "Home") {
    event.preventDefault();
    activeIndex.value = 0;
  } else if (event.key === "End") {
    event.preventDefault();
    activeIndex.value = last;
  } else if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
    event.preventDefault();
    if (activeIndex.value >= 0) {
      const option = filteredOptions.value[activeIndex.value];
      if (option) selectOption(option);
    }
  } else if (event.key === "Escape") {
    event.preventDefault();
    close(true);
  } else if (event.key === "Tab") {
    close();
  } else if (event.key.length === 1) {
    typeahead(event.key);
  }
};

// The search input needs its own keydown handler: unlike the plain listbox,
// it's a text field, so printable characters (including space) must be left
// alone to type normally -- e.g. a multi-word query like "human systems".
// Only navigation keys are handled here; Home/End are intentionally left to
// the browser's native text-cursor behavior, and typeahead() is not invoked
// since the search input already filters via onSearchInput.
const onSearchKeydown = (event: KeyboardEvent): void => {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    const last = filteredOptions.value.length - 1;
    activeIndex.value = Math.min(last, activeIndex.value + 1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    activeIndex.value = Math.max(0, activeIndex.value - 1);
  } else if (event.key === "Enter") {
    event.preventDefault();
    const option = filteredOptions.value[activeIndex.value];
    if (option) selectOption(option);
  } else if (event.key === "Escape") {
    event.preventDefault();
    close(true);
  }
};

const onDocumentClick = (event: MouseEvent): void => {
  if (isOpen.value && rootRef.value && !rootRef.value.contains(event.target as Node)) {
    close();
  }
};

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  if (typeTimer) clearTimeout(typeTimer);
});
</script>

<style scoped lang="scss">
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.calculator-select {
  position: relative;
  display: inline-flex;
}

.calculator-select__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 14rem;
  padding: 0 0.15rem 0.15rem;
  border: 0;
  background: transparent;
  color: var(--rds-dark-3, #747474);
  font-size: clamp(1.35rem, 2.5vw, 2rem);
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.calculator-select__trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.calculator-select__value {
  flex: 1 1 auto;

  border-bottom: 4px solid var(--rds-dark-3, #747474);
}

.calculator-select__value.is-placeholder {
  color: var(--rds-dark-2, #747474);
  border-bottom-color: var(--rds-dark-2, #747474);
}

.chevron-icon {
  width: 1rem;
}

.calculator-select__chevron {
  flex: 0 0 auto;
  width: 0;
  height: 0;
  border-left: 0.35em solid transparent;
  border-right: 0.35em solid transparent;
  border-top: 0.4em solid currentColor;
}

.calculator-select__popup {
  position: absolute;
  top: 100%;
  left: 0;
  box-sizing: border-box;
  z-index: 20;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  margin: 0.25rem 0 0;
  background: #fff;
  border: 1px solid var(--rds-border-color, #d8d8d8);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.12);
}

.calculator-select__search {
  position: relative;
  padding: 0.5rem;
  border-bottom: 1px solid var(--rds-border-color, #d8d8d8);
}

.calculator-select__search-icon {
  position: absolute;
  top: 50%;
  left: 1.25rem;
  width: 1rem;
  height: 1rem;
  color: var(--rds-dark-3, #191919);
  transform: translateY(-50%);
  pointer-events: none;
}

.calculator-select__search-input {
  width: 100%;
  padding: 0.4rem 0.5rem 0.4rem 2.25rem;
  font-size: 1rem;
  border: 1px solid var(--rds-border-color, #c8c8c8);
}

.calculator-select__search-input:focus {
  outline: 0;
  border: 2px solid var(--rds-dark-3, #191919);
}

.calculator-select__listbox {
  position: static;
  max-height: min(18rem, 52vh);
  margin: 0;
  padding: 0.25rem 0;
  overflow-y: auto;
  list-style: none;
  border: 0;
  box-shadow: none;
}

.calculator-select__empty {
  margin: 0;
  padding: 0.75rem 1rem;
  color: var(--rds-dark-2, #747474);
}

.calculator-select__option {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 1rem;
  color: var(--rds-dark-3, #4d4d4d);
  font-size: 1rem;
  font-weight: 400;
  cursor: pointer;
}

.calculator-select__option.is-active,
.calculator-select__option:hover {
  background: var(--rds-light-2, #e8e8e8);
}

.calculator-select__option.is-selected {
  background: var(--rds-secondary, #ffc627);
}

.calculator-select__option-text {
  font-weight: 400;
}

.calculator-select__option.is-selected .calculator-select__option-text {
  font-weight: 700;
}

.calculator-select__option-description {
  color: var(--rds-dark-2, #747474);
  font-size: 0.875rem;
}

@media (max-width: 575.98px) {
  .calculator-select {
    width: 100%;
  }

  .calculator-select__trigger {
    width: 100%;
    min-width: 0;
  }

  .calculator-select__popup {
    max-height: min(16rem, 42dvh);
    overflow: hidden;
  }

  .calculator-select__listbox {
    max-height: min(12rem, 32dvh);
    overscroll-behavior: contain;
  }
}
</style>
